import { TitleCasePipe, DatePipe, DecimalPipe } from "@angular/common";
import { Component, OnInit, OnDestroy, inject, signal, WritableSignal, computed } from "@angular/core";
import { MatDialogRef, MAT_DIALOG_DATA } from "@angular/material/dialog";
import { Router } from "@angular/router";
import { Subject, takeUntil, take } from "rxjs";
import { UserPlanType, InvoiceDto } from "../../../../api/models/fraud.models";
import { dialogRiseAnimation } from "../../../animations/pricing-animations";
import { PlanTemplatesService } from "../../../services/plan-templates.service";
import { VariablesService } from "../../../services/variables.service";
import { BillingType } from "../dialog-purchase-flow/dialog-purchase-flow.component";
import { InvoicePlanUpgradeComponent } from "../invoices/invoice-plan-upgrade/invoice-plan-upgrade.component";
import { AuthService } from "../../../services/auth.service";
import { ApiService } from "../../../../api/services/api.service";

export interface RenewalData {
  tier?: UserPlanType;
  billing?: BillingType;
  showHideBanner?: boolean;
  daysLeft?: number;
  expiryDate?: string;
  balanceUsed?: string;
  balanceTotal?: string;
  balanceProgress?: number;
  complianceUsed?: string;
  complianceTotal?: string;
  complianceProgress?: number;
  forfeitedBalance?: string;
}

export type RenewalDialogCloseAction = 'dismiss' | 'close' | 'toast';

export interface RenewalDialogClosePayload {
  action: RenewalDialogCloseAction;
  source: 'button' | 'esc' | 'backdrop';
}

@Component({
  selector: 'app-dialog-renewal',
  imports: [
    TitleCasePipe,
    DatePipe,
    InvoicePlanUpgradeComponent,
    DecimalPipe
],
  templateUrl: './dialog-renewal.component.html',
  styleUrl: './dialog-renewal.component.scss',
  animations: [dialogRiseAnimation],
  host: {
    class: 'block h-full min-h-0',
  },
})
export class DialogRenewalComponent implements OnInit, OnDestroy {
  readonly dialogRef = inject(MatDialogRef<DialogRenewalComponent>);
  readonly data: {showHideBanner?: boolean} = inject(MAT_DIALOG_DATA);
  public readonly authService: AuthService = inject(AuthService);
  public readonly router: Router = inject(Router);
  public readonly _apiService: ApiService = inject(ApiService);
  private readonly _planTemplatesService = inject(PlanTemplatesService);
  private readonly _destroy$ = new Subject<void>();
  readonly variablesService = inject(VariablesService);

  public readonly planTemplates = this._planTemplatesService.planTemplates;
  public readonly disabledButtons = signal<boolean>(false);
  public readonly stage: WritableSignal<'renew' | 'payment'> = signal<'renew' | 'payment'>('renew');
  public readonly invoiceUuid: WritableSignal<string> = signal<string>('');
  public readonly invoiceStatus: WritableSignal<InvoiceDto['status']> = signal<InvoiceDto['status']>('WAITING_FOR_PAYMENT');

  readonly corporateAnnualPrice = computed(() => {
    const corporatePlan = this.planTemplates().find((p) => p.type === 'CORPORATE');
    const yearly = corporatePlan?.planPriceYearly;
    return yearly ?? 0;
  });

  public readonly activatedPlan = this.authService.effectiveUserPlan;

  actualPlanForTemplate = computed(() => {
    const activatedOtExpiredPlan = this.activatedPlan();
    return this.planTemplates().find((p) => p.type === activatedOtExpiredPlan?.userPlanType);
  });

  readonly complianceProgress = computed(() => {
    const complianceHoursUsed = this.activatedPlan()?.complianceHoursUsed ?? 0;
    const complianceHoursGranted = this.activatedPlan()?.complianceHoursGranted ?? 0;
    return (complianceHoursUsed / complianceHoursGranted * 100);
  });

  readonly balanceProgress = computed(() => {
    const balanceUsed = this.activatedPlan()?.balanceUsed ?? 0;
    const planPrice = this.activatedPlan()?.planPrice ?? 0;
    return (balanceUsed / planPrice * 100);
  });

  readonly expiredDatePlan = computed(() => {
    const expirationDate = this.activatedPlan()?.expirationDate;
    if (!expirationDate) {
      return false;
    }
    return Date.parse(expirationDate) < Date.now();
  });

  readonly daysLeft = computed(() => {
    const periodStart = this.activatedPlan()?.periodStart;
    const expirationDate = this.activatedPlan()?.expirationDate;
    if (!periodStart || !expirationDate) {
      return 0;
    }
    const startMs = Date.parse(periodStart);
    const endMs = Date.parse(expirationDate);
    const todayMs = Date.now();
    const totalDays = Math.round((endMs - startMs) / (1000 * 60 * 60 * 24));
    const elapsedDays = Math.round((todayMs - startMs) / (1000 * 60 * 60 * 24));
    return totalDays - elapsedDays;
  });

  ngOnInit(): void {
    this._planTemplatesService.startPlanTemplatesSync();
    this.dialogRef.disableClose = true;
    this.dialogRef.backdropClick().pipe(takeUntil(this._destroy$)).subscribe(() => {
      this.close({ action: 'close', source: 'backdrop' });
    });
    this.dialogRef.keydownEvents().pipe(takeUntil(this._destroy$)).subscribe((event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        this.close({ action: 'close', source: 'esc' });
      }
    });
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  public renew(): void {
    const body = {
      type: this.activatedPlan()?.userPlanType as UserPlanType,
      billingCycle: this.activatedPlan()?.billingCycle ?? 'MONTHLY',
    };
    this.disabledButtons.set(true);
    const activatedPlan = this.activatedPlan();
    const isExpired = activatedPlan?.expirationDate ? Date.parse(activatedPlan?.expirationDate) < Date.now() : false;
    let action = 'renew';
    if (isExpired) {action = 'purchase'};
    switch (action) {
      case 'renew':
        this._apiService.postRenewUserPlan(body).pipe(take(1)).subscribe({
          next: (response: {invoiceUuid: string}) => {
            this.stage.set('payment');
            this.invoiceUuid.set(response.invoiceUuid);
            this.disabledButtons.set(false);
          },
          error: (error) => {
            console.error('postRenewUserPlan', error)
            this.disabledButtons.set(false);
          },
        });
        break;
      case 'purchase':
        this._apiService.postUserPlanPurchase(body).pipe(take(1)).subscribe({
          next: (response: {invoiceUuid: string}) => {
            this.stage.set('payment');
            this.invoiceUuid.set(response.invoiceUuid);
            this.disabledButtons.set(false);
          },
          error: (error) => {
            console.error('postRenewUserPlan', error)
            this.disabledButtons.set(false);
          },
        });
        break;
    }
  }

  public dontShowAgain(): void {
    this.close({ action: 'dismiss', source: 'button' });
  }

  public redirectToPricing(): void {
    this.close({ action: 'close', source: 'button' });
    this.router.navigate(['/pricing']);
  }

  public close(payload: RenewalDialogClosePayload = { action: 'close', source: 'button' }): void {
    console.log('payload', payload);
    if (payload.action !== 'dismiss' && this.stage() === 'payment') {
      console.log('payload', payload);
      if (this.invoiceStatus() === 'WAITING_FOR_PAYMENT') {
        this.dialogRef.close({action: 'toast', source: 'button'});
      } else {
        this.dialogRef.close(payload);
      }
    } else {
      this.dialogRef.close(payload);
    }
    this.variablesService.triggerBannersReload();
  }
}
